import React, { useMemo } from 'react';
import { useCMS, PublicPageType } from '../../context/CMSContext';
import { BrandLogo } from '../common/BrandLogo';
import { ScrollReveal } from '../common/ScrollReveal';
import { SocialIconRenderer } from '../common/SocialIconRenderer';

export const Footer: React.FC = () => {
  const { data, setActivePublicPage } = useCMS();
  const { siteSettings, navigation } = data;

  const navigateToPage = (page: PublicPageType, sectionId?: string) => {
    setActivePublicPage(page);
    if (sectionId) {
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 50);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Resolve dynamic social channels with graceful legacy fallback
  const activeSocials = useMemo(() => {
    if (siteSettings?.socialLinks && siteSettings.socialLinks.length > 0) {
      return siteSettings.socialLinks.filter(
        (s) => s.isEnabled !== false && s.url && s.url.trim() !== ''
      );
    }

    const fallback = [];
    if (siteSettings?.socials?.instagram) {
      fallback.push({
        id: 'soc-ig',
        platform: 'instagram',
        label: 'Instagram',
        url: siteSettings.socials.instagram,
        isEnabled: true,
        openInNewTab: true
      });
    }
    if (siteSettings?.socials?.linkedin) {
      fallback.push({
        id: 'soc-li',
        platform: 'linkedin',
        label: 'LinkedIn',
        url: siteSettings.socials.linkedin,
        isEnabled: true,
        openInNewTab: true
      });
    }
    if (siteSettings?.email) {
      fallback.push({
        id: 'soc-mail',
        platform: 'email',
        label: 'Email Enquiries',
        url: `mailto:${siteSettings.email}`,
        isEnabled: true,
        openInNewTab: false
      });
    }
    return fallback;
  }, [siteSettings?.socialLinks, siteSettings?.socials, siteSettings?.email]);

  const footerBg = siteSettings?.footerBgColor || siteSettings?.headerBgColor || '#0a122a';

  return (
    <footer 
      id="connect-footer" 
      style={{ backgroundColor: footerBg }}
      className="text-[#a09e99] py-7 md:py-8 border-t border-white/10 font-sans select-none w-full max-w-full overflow-hidden transition-colors duration-300"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 xl:px-10 w-full">
        <ScrollReveal delay={50}>
          {/* Main Footer Container - Responsive Luxury Row */}
          <div className="flex flex-col xl:flex-row items-center justify-between gap-6 xl:gap-4 2xl:gap-8 w-full">
          
            {/* 1. Logo & Brand */}
            <div className="flex-shrink-0">
              <button
                onClick={() => navigateToPage('home')}
                className="inline-block group cursor-pointer text-left focus:outline-none"
              >
                <BrandLogo variant="footer" />
              </button>
            </div>

            {/* 2. Navigation Links (HOME, PROJECTS, ABOUT US, JOURNAL, CONNECT) */}
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-5 xl:gap-6 text-[11px] sm:text-[12px] tracking-[0.14em] sm:tracking-[0.18em] xl:tracking-[0.20em] text-[#d6d4ce] uppercase font-sans font-medium whitespace-nowrap">
              <button
                onClick={() => navigateToPage('home')}
                className="hover:text-[#a88345] transition-colors cursor-pointer"
              >
                {navigation?.home || 'HOME'}
              </button>
              <button
                onClick={() => navigateToPage('projects')}
                className="hover:text-[#a88345] transition-colors cursor-pointer"
              >
                {navigation?.projects || 'PROJECTS'}
              </button>
              <button
                onClick={() => navigateToPage('about')}
                className="hover:text-[#a88345] transition-colors cursor-pointer"
              >
                {navigation?.aboutUs || 'ABOUT US'}
              </button>
              <button
                onClick={() => navigateToPage('journal')}
                className="hover:text-[#a88345] transition-colors cursor-pointer"
              >
                {navigation?.journal || 'JOURNAL'}
              </button>
              <button
                onClick={() => navigateToPage('connect')}
                className="hover:text-[#a88345] transition-colors cursor-pointer"
              >
                {navigation?.connect || 'CONNECT'}
              </button>
            </div>

            {/* 3. Reach Out Info (Conditionally Rendered based on Central Master Settings) */}
            {siteSettings?.showFooterReachOut !== false && (
              <>
                {/* Vertical Divider 1 */}
                <div className="hidden xl:block h-10 w-px bg-white/15 flex-shrink-0" />

                <div className="space-y-1 text-xs text-[#9ca3af] text-center xl:text-left min-w-[140px] flex-shrink-0">
                  <div className="text-white font-medium text-[12px] tracking-wide">
                    {siteSettings?.footerReachOutHeading || 'Reach Out'}
                  </div>
                  <div>
                    <a
                      href={`mailto:${siteSettings?.email || 'enquiries@oltraya.com'}`}
                      className="hover:text-[#a88345] transition-colors text-[11px] block truncate"
                      title={siteSettings?.email || 'enquiries@oltraya.com'}
                    >
                      {siteSettings?.email || 'enquiries@oltraya.com'}
                    </a>
                  </div>
                  <div>
                    <a
                      href={`tel:${(siteSettings?.phone || '+91 (020) 6829 4400').replace(/[^+\d]/g, '')}`}
                      className="hover:text-[#a88345] transition-colors text-[11px] block"
                    >
                      {siteSettings?.phone || '+91 (020) 6829 4400'}
                    </a>
                  </div>
                </div>
              </>
            )}

            {/* Vertical Divider 2 */}
            <div className="hidden xl:block h-10 w-px bg-white/15 flex-shrink-0" />

            {/* 4. Legal / Sub-pages Links - Stacked Vertically */}
            <div className="flex flex-col space-y-1 text-center xl:text-left min-w-[135px] flex-shrink-0 text-xs text-[#9ca3af]">
              <button
                onClick={() => navigateToPage('privacy')}
                className="hover:text-[#a88345] transition-colors cursor-pointer text-center xl:text-left text-[11px] block whitespace-nowrap"
              >
                Privacy Policy
              </button>
              <button
                onClick={() => navigateToPage('investor-group')}
                className="hover:text-[#a88345] transition-colors cursor-pointer text-center xl:text-left text-[11px] block whitespace-nowrap"
              >
                Investor &amp; Landowner Group
              </button>
              <button
                onClick={() => navigateToPage('terms')}
                className="hover:text-[#a88345] transition-colors cursor-pointer text-center xl:text-left text-[11px] block whitespace-nowrap"
              >
                Terms
              </button>
            </div>

            {/* 5. Copyright & Dynamic Social Icons */}
            <div className="flex flex-col items-center xl:items-end space-y-1.5 flex-shrink-0">
              <div className="text-xs text-[#9ca3af] tracking-wide text-center xl:text-right leading-tight whitespace-nowrap">
                <div className="text-[11px] text-[#9ca3af]">{siteSettings?.copyrightText || '© 2026 OLTRAYA REALTY.'}</div>
                <div className="text-[10px] text-[#71717a] mt-0.5">{siteSettings?.allRightsReservedText || 'All Right Reserved'}</div>
              </div>

              {/* Circular Social Outline Icons */}
              <div className="flex items-center flex-wrap gap-1.5 pt-0.5 justify-center xl:justify-end">
                {activeSocials.map((social) => {
                  const isMailOrTel = social.url.startsWith('mailto:') || social.url.startsWith('tel:');
                  return (
                    <a
                      key={social.id}
                      href={social.url}
                      target={!isMailOrTel && social.openInNewTab !== false ? '_blank' : undefined}
                      rel={!isMailOrTel && social.openInNewTab !== false ? 'noreferrer noopener' : undefined}
                      className="w-7 h-7 rounded-full border border-white/25 flex items-center justify-center text-white/80 hover:text-[#a88345] hover:border-[#a88345] transition-all"
                      aria-label={social.label || social.platform}
                      title={social.label || social.platform}
                    >
                      <SocialIconRenderer platformOrIcon={social.platform} className="w-3.5 h-3.5" />
                    </a>
                  );
                })}
              </div>
            </div>

          </div>
        </ScrollReveal>
      </div>
    </footer>
  );
};
